import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Globe, RefreshCw, Plus, Car, Bus, Truck, TrendingUp, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';

const SERVICES = [
  { key: 'rides', label: 'Rides', Icon: Car, tint: 'text-indigo-600' },
  { key: 'bus', label: 'Bus', Icon: Bus, tint: 'text-amber-600' },
  { key: 'logistics', label: 'Logistics', Icon: Truck, tint: 'text-emerald-600' },
];
const PERIODS = [
  { v: 7, label: '7d' },
  { v: 30, label: '30d' },
  { v: 90, label: '90d' },
];

const fmtK = (n) => `K${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const AdminCities = () => {
  const [cities, setCities] = useState([]);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newCity, setNewCity] = useState({ slug: '', name: '' });

  const fetchCities = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cities?period_days=${period}`);
      setCities(data.items || []);
    } catch (err) {
      toast({ title: 'Failed to load cities', variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchCities(); /* eslint-disable-next-line */ }, [period]);

  const toggle = async (slug, service, value) => {
    setCities((prev) => prev.map(c => c.slug === slug ? { ...c, enabled: { ...c.enabled, [service]: value } } : c));
    try {
      await api.put(`/admin/cities/${slug}`, { [service]: value });
      toast({ title: `${service} ${value ? 'enabled' : 'disabled'} in ${slug}` });
    } catch {
      toast({ title: 'Update failed', variant: 'destructive' });
      fetchCities();
    }
  };

  const createCity = async () => {
    if (!newCity.slug || !newCity.name) {
      toast({ title: 'Slug and name required', variant: 'destructive' });
      return;
    }
    try {
      await api.post('/admin/cities', newCity);
      toast({ title: `${newCity.name} added` });
      setShowAdd(false); setNewCity({ slug: '', name: '' });
      fetchCities();
    } catch (err) {
      toast({
        title: 'Failed to add city',
        description: err.response?.data?.detail || 'Failed',
        variant: 'destructive',
      });
    }
  };

  const deleteCity = async (city) => {
    if (!window.confirm(`Delete ${city.name}? This only works if the city has no revenue in the last 30 days.`)) return;
    try {
      await api.delete(`/admin/cities/${city.slug}`);
      toast({ title: `${city.name} removed` });
      fetchCities();
    } catch (err) {
      toast({
        title: 'Cannot delete',
        description: err.response?.data?.detail || 'Failed',
        variant: 'destructive',
      });
    }
  };

  const totals = cities.reduce(
    (acc, c) => ({
      rides: acc.rides + (c.revenue?.rides || 0),
      bus: acc.bus + (c.revenue?.bus || 0),
      logistics: acc.logistics + (c.revenue?.logistics || 0),
      total: acc.total + (c.revenue?.total || 0),
    }), { rides: 0, bus: 0, logistics: 0, total: 0 },
  );

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-cities-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Globe className="h-6 w-6 text-sky-500" /> Cities
            </h1>
            <p className="text-sm text-gray-500">
              Per-city service toggles &amp; revenue breakdown. Disable a service to soft-launch a city without releasing new code.
            </p>
          </div>
          <div className="flex gap-2">
            <div className="flex bg-white border rounded-md overflow-hidden" data-testid="cities-period-picker">
              {PERIODS.map(p => (
                <button
                  key={p.v} onClick={() => setPeriod(p.v)}
                  className={`px-3 py-1.5 text-xs font-medium ${period === p.v ? 'bg-sky-500 text-white' : 'text-gray-600'}`}
                  data-testid={`cities-period-${p.v}`}
                >{p.label}</button>
              ))}
            </div>
            <Button variant="outline" onClick={fetchCities} data-testid="cities-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button onClick={() => setShowAdd(true)} data-testid="cities-add-btn">
              <Plus className="h-4 w-4 mr-1" /> Add city
            </Button>
          </div>
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cities-totals-strip">
          {[
            { label: 'Total revenue', value: totals.total, Icon: TrendingUp, tint: 'text-sky-600' },
            { label: 'Rides', value: totals.rides, Icon: Car, tint: 'text-indigo-600' },
            { label: 'Bus', value: totals.bus, Icon: Bus, tint: 'text-amber-600' },
            { label: 'Logistics', value: totals.logistics, Icon: Truck, tint: 'text-emerald-600' },
          ].map(({ label, value, Icon, tint }) => (
            <Card key={label} className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
                <Icon className={`h-4 w-4 ${tint}`} />
              </div>
              <p className="text-xl font-bold mt-1">{fmtK(value)}</p>
            </Card>
          ))}
        </div>

        {/* Cities grid */}
        {loading && cities.length === 0 ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="cities-grid">
            {cities.map(c => (
              <Card key={c.slug} className="p-5 space-y-4" data-testid={`city-card-${c.slug}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-lg flex items-center gap-2">{c.name}</h3>
                    <p className="text-xs text-gray-500 font-mono">{c.slug}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-bold">{fmtK(c.revenue?.total)}</p>
                    <button
                      onClick={() => deleteCity(c)}
                      className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                      title="Delete city (only allowed if 0 revenue)"
                      data-testid={`city-${c.slug}-delete-btn`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs">
                  {SERVICES.map(({ key, label, Icon, tint }) => (
                    <div key={key} className="border rounded p-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Icon className={`h-3.5 w-3.5 ${tint}`} />
                        <span className="text-gray-600">{label}</span>
                      </div>
                      <p className="font-semibold mt-1" data-testid={`city-${c.slug}-${key}-rev`}>
                        {fmtK(c.revenue?.[key])}
                      </p>
                      <p className="text-[10px] text-gray-400">{c.revenue?.[`${key}_count`] || 0} bkgs</p>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-3 space-y-2">
                  {SERVICES.map(({ key, label, Icon, tint }) => (
                    <div key={key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm">
                        <Icon className={`h-4 w-4 ${tint}`} />
                        <span>{label}</span>
                      </div>
                      <Switch
                        checked={!!c.enabled?.[key]}
                        onCheckedChange={(v) => toggle(c.slug, key, v)}
                        data-testid={`city-${c.slug}-toggle-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Add city modal */}
        {showAdd && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowAdd(false)}>
            <Card className="p-5 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()} data-testid="cities-add-modal">
              <h3 className="font-bold">Add city</h3>
              <div>
                <Label>Slug (lowercase, no spaces)</Label>
                <Input
                  value={newCity.slug}
                  onChange={(e) => setNewCity({ ...newCity, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="mansa"
                  data-testid="cities-add-slug"
                />
              </div>
              <div>
                <Label>Display name</Label>
                <Input
                  value={newCity.name}
                  onChange={(e) => setNewCity({ ...newCity, name: e.target.value })}
                  placeholder="Mansa"
                  data-testid="cities-add-name"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button onClick={createCity} data-testid="cities-add-submit">Add</Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCities;
